import React, { useState } from 'react';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useDispatch, useSelector } from 'react-redux';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import DataGridMultiBrokerGroup from '@/components/table/DataGridComponents/DataGridMultiBrokerGroup';
import { ExpandableChips } from '@/components/table/DataGridComponents/ExpandableChips';
import { SHARE_FORM_CONFIG_DIALOG } from '@/components/dialogs/ShareFormConfigDialog';
import { showToast } from '@/features/toast/toastSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useBatchUnlinkFormConfigMutation } from '@/features/config/configApi';
import { SCOPES } from '@/features/config/scopes';

/**
 * How a row is shared: for a shared row, the group it's shared from and a button that breaks
 * the link (after a confirm), making it an ordinary config the group can edit; for one of the
 * user's groups' own configs, chips of the groups it's shared with (editing it changes theirs
 * too) on one line that expands, or "Not shared", and clicking opens the share dialog to
 * change who it's shared with. Empty for ledger and default rows.
 */
const SharedCell = ({ config, disabled }) => {
	const dispatch = useDispatch();
	const [isConfirmOpen, setIsConfirmOpen] = useState(false);
	const [batchUnlinkFormConfig] = useBatchUnlinkFormConfigMutation();

	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { sharedFrom, sharedWith } = config;

	// An original (the user's group's own config): clicking opens the share dialog with the
	// groups it's shared with ticked, to change them, as in the admin portal
	if (config.scope === SCOPES.BROKER_GROUP && !sharedFrom) {
		const openShareDialog = () =>
			dispatch(
				openDialog({
					dialogName: SHARE_FORM_CONFIG_DIALOG,
					data: {
						docIds: [config.id],
						configType: config.configType,
						originalGroupIds: [config.scopeId],
						skipped: 0,
						sharedWith: sharedWith ?? [],
					},
				}),
			);
		return (
			<Stack
				onClick={(event) => {
					event.stopPropagation();
					if (!disabled) openShareDialog();
				}}
				sx={{
					cursor: disabled ? 'default' : 'pointer',
					width: '100%',
					height: '100%',
					justifyContent: 'center',
				}}
			>
				{/* sharedWith is [{ id, name }] from the list; one line that expands, like the
				admin portal's broker group cells */}
				{sharedWith ? (
					<ExpandableChips
						items={sharedWith}
						renderChip={({ id, name }) => <Chip key={id} size='small' label={name} />}
						itemsLabel='broker groups'
					/>
				) : (
					<Typography variant='body2' color='text.disabled'>
						Not shared
					</Typography>
				)}
			</Stack>
		);
	}
	if (!sharedFrom) return null;

	const handleUnlink = async () => {
		const result = await batchUnlinkFormConfig({
			docIds: [config.id],
			configType: config.configType,
		});
		setIsConfirmOpen(false);
		if (result.error) {
			dispatch(
				showToast({ message: "Couldn't break the link. Please try again.", error: true }),
			);
			return;
		}
		dispatch(showToast({ message: 'Broke the link; the row can now be edited', success: true }));
	};

	return (
		<Stack
			direction='row'
			spacing={0.5}
			sx={{ alignItems: 'center', justifyContent: 'space-between', width: '100%', minWidth: 0 }}
		>
			<Stack direction='row' spacing={0.5} sx={{ alignItems: 'center', minWidth: 0 }}>
				<Typography variant='body2' sx={{ flexShrink: 0 }}>
					Shared from
				</Typography>
				<DataGridMultiBrokerGroup
					brokerGroupIds={[sharedFrom.scopeId]}
					showAdmin={userDetails?.isFolioAdmin}
				/>
			</Stack>
			<Tooltip title='Break link to make this an editable copy'>
				<span>
					<IconButton
						size='small'
						aria-label='Break link to make this an editable copy'
						disabled={disabled}
						onClick={() => setIsConfirmOpen(true)}
					>
						<LinkOffIcon fontSize='small' />
					</IconButton>
				</span>
			</Tooltip>
			<ConfirmDialog
				openDialog={isConfirmOpen}
				handleClose={() => setIsConfirmOpen(false)}
				handleConfirm={handleUnlink}
				title='Break link?'
				content={`${config.label || config.name} keeps its current settings and becomes your group's own config, which you can edit. It won't follow changes to the original any more.`}
				confirmText='Break link'
			/>
		</Stack>
	);
};

export default SharedCell;
